import { RobotRead } from 'api/schema'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import { IOverlayItem } from 'components/MapboxMap/OverlayControl/OverlayControl'
import UndoMapButton from 'components/MapboxMap/UndoMapButton/UndoMapButton'
import RobotOnMap from 'components/RobotOnMap/RobotOnMap'
import MissionScenarioServicePointsOnMap from 'components/ScenarioServicePointsOnMap/MissionScenarioServicePointsOnMap'
import { Feature, GeoJsonProperties, Geometry } from 'geojson'
import LidarMapLayer from 'pages/Robot/RobotView/Layers/LidarMapLayer'
import React, { useImperativeHandle, useState } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { Layer, MapboxMap } from 'react-map-gl'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import { RobotActions } from 'store/actions/robot'
import {
  selectGeoTiffLayer,
  selectMission,
  selectMode,
  selectSavingZoneProgress,
  selectShowCleaningZones,
  selectShowRestrictZones,
} from 'store/selectors/mission'
import { selectRobotMapLocalization } from 'store/selectors/robot'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { robotThunks } from 'store/thunks/robot'
import {
  MISSION_MAP_DRAWING_MODES,
  MISSION_PAGE_MODES,
  PROGRESS_TYPES,
  TRANSFER_CALCULATING_STATUSES,
} from 'utils/constants'
import { IMission, ITrajectory, RootState } from 'utils/interfaces'
import { IMapboxElement } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'

import DrawControl from './DrawControl/DrawControl'
import drawStyles from './DrawControl/drawStyles'
import ActiveRouteBufferOnMap from './Layers/ActiveRouteBufferOnMap'
import CleaningPointsOnMap from './Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from './Layers/CleaningRoutesOnMap'
import CleaningZonesNamesOnMap from './Layers/CleaningZonesNamesOnMap'
import CleaningZonesOnMap from './Layers/CleaningZonesOnMap'
import GeoTiffLayer from './Layers/GeoTiffLayer'
import MissionStartFinishPoints from './Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from './Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from './Layers/TransferRoutesOnMap'

let globalLastZoomedLayerId: string | number | undefined = undefined

const EDITING_MODES = new Set([
  MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE,
  MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE,
  MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE,
  MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_LAUNCH_POINT,
  MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT,
  MISSION_MAP_DRAWING_MODES.DRAW_ROUTE,
  MISSION_MAP_DRAWING_MODES.DRAW_POLYGON,
  MISSION_MAP_DRAWING_MODES.ADD_ZONE_LAUNCH_POINT,
  MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT,
  MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT,
])

interface MissionMapProps {
  children?: React.ReactNode
  selectedRobot?: RobotRead
  onSelectRobotClick?: () => void
  missions?: IMission[]
  highlightedMissionId?: number
  showLidarMapOption?: boolean
  disableOverlayControl?: boolean
  disableRuler?: boolean
  disableSearch?: boolean
  searchRight?: number
  panelOffset?: number
  extraOverlayItems?: IOverlayItem[]
  /** `view` is Mission Control: same layers, without draw/undo and robot picking. */
  interaction?: 'view' | 'edit'
  /**
   * View mode with no active mission keeps the map shell (lidar, robot, laserscan)
   * and does not mount zone, route, or service-point sources.
   * Editor and a view with a mission leave this on.
   */
  mountMissionLayers?: boolean
}

const EDIT_UNDO_MODES = new Set([
  MISSION_PAGE_MODES.EDIT_ZONE_ROUTE,
  MISSION_PAGE_MODES.DRAW_ROUTE,
  MISSION_PAGE_MODES.DRAW_ZONE,
  MISSION_PAGE_MODES.EDIT_ZONE,
  MISSION_PAGE_MODES.ADJUST_COVERAGE,
])

/** Mounted only in the mission editor, so Mission Control does not subscribe to undo history. */
const MissionMapUndoControls = () => {
  const mode = useSelector(selectMode)
  const canUndo = useSelector((state: RootState) => {
    const pageMode = state.mission.mode
    if (pageMode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || pageMode === MISSION_PAGE_MODES.DRAW_ROUTE) {
      return state.mission.tempZoneRouteHistory.length > 0
    }
    if (
      pageMode === MISSION_PAGE_MODES.DRAW_ZONE ||
      pageMode === MISSION_PAGE_MODES.EDIT_ZONE ||
      pageMode === MISSION_PAGE_MODES.ADJUST_COVERAGE
    ) {
      return state.mission.tempZoneHistory.length > 0
    }
    return false
  })
  const canRedo = useSelector((state: RootState) => {
    const pageMode = state.mission.mode
    if (pageMode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || pageMode === MISSION_PAGE_MODES.DRAW_ROUTE) {
      return state.mission.tempZoneRouteFutureHistory.length > 0
    }
    if (
      pageMode === MISSION_PAGE_MODES.DRAW_ZONE ||
      pageMode === MISSION_PAGE_MODES.EDIT_ZONE ||
      pageMode === MISSION_PAGE_MODES.ADJUST_COVERAGE
    ) {
      return state.mission.tempZoneFutureHistory.length > 0
    }
    return false
  })
  const savingProgressInWork = useSelector(selectSavingZoneProgress) === PROGRESS_TYPES.WORK

  if (!EDIT_UNDO_MODES.has(mode)) return null

  const handleUndo = () => {
    if (mode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || mode === MISSION_PAGE_MODES.DRAW_ROUTE) {
      store.dispatch(missionThunks.undoRouteEdit())
    } else if (
      mode === MISSION_PAGE_MODES.DRAW_ZONE ||
      mode === MISSION_PAGE_MODES.EDIT_ZONE ||
      mode === MISSION_PAGE_MODES.ADJUST_COVERAGE
    ) {
      store.dispatch(missionThunks.undoZoneEdit())
    }
  }

  const handleRedo = () => {
    if (mode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || mode === MISSION_PAGE_MODES.DRAW_ROUTE) {
      store.dispatch(missionThunks.redoRouteEdit())
    } else if (
      mode === MISSION_PAGE_MODES.DRAW_ZONE ||
      mode === MISSION_PAGE_MODES.EDIT_ZONE ||
      mode === MISSION_PAGE_MODES.ADJUST_COVERAGE
    ) {
      store.dispatch(missionThunks.redoZoneEdit())
    }
  }

  return (
    <UndoMapButton
      undoDisabled={!canUndo || savingProgressInWork}
      onUndo={handleUndo}
      redoDisabled={!canRedo || savingProgressInWork}
      onRedo={handleRedo}
    />
  )
}

const MissionMap = React.memo(
  React.forwardRef<IMapboxElement, MissionMapProps>((props, ref) => {
    const dispatch = useDispatch<any>()
    const mapRef = useRef<IMapboxElement>(null)
    const [map, setMap] = useState<MapboxMap | undefined>(undefined)
    const mission = useSelector(selectMission)
    const showCleaningZones = useSelector(selectShowCleaningZones)
    const showRestrictZones = useSelector(selectShowRestrictZones)
    const geoTiffLayer = useSelector(selectGeoTiffLayer)
    const robotLoc = useSelector(selectRobotMapLocalization, shallowEqual)
    const lidarMapLayer = useSelector((state: any) => state.robot.lidarMapLayer)
    const zoomOffset = props.panelOffset ?? 580
    const isView = props.interaction === 'view'
    const mountMissionLayers = props.mountMissionLayers ?? true

    const onFeatureCreated = (evt: { features: Feature[] }) => {
      store.dispatch(missionThunks.createFeature(evt))
    }

    const onFeatureGeometryEdited = (evt: { features: Feature[]; action: string }) => {
      store.dispatch(missionThunks.editFeature(evt))
    }

    useImperativeHandle(ref, () => ({
      getMap() {
        return mapRef?.current?.getMap()
      },
    }))

    // Track which zone/route we've already zoomed to, so we don't re-zoom on every geometry edit
    const zoomedRouteIdRef = useRef<number | string | null>(null)
    const zoomedZoneIdRef = useRef<number | string | null>(null)
    const zoomedMissionIdRef = useRef<number | string | null>(null)

    useEffect(() => {
      if (isView) return
      const map = mapRef.current?.getMap()
      if (!map) return

      // When the user is actively drawing a new route or zone, never auto-zoom.
      // This prevents the map from jumping when the first point is placed (single-point bbox).
      // We use mission.mode (page-level) because it is set synchronously when the button is
      // clicked and remains stable throughout the entire drawing session.
      const isDrawingNew =
        mission.mapMode === MISSION_MAP_DRAWING_MODES.DRAW_ROUTE ||
        mission.mapMode === MISSION_MAP_DRAWING_MODES.DRAW_POLYGON
      if (isDrawingNew) {
        // Keep refs null so that when the user FINISHES drawing and enters an editing mode,
        // the zoom to the completed feature will still fire.
        return
      }

      const isEditing = EDITING_MODES.has(mission.mapMode)
      let featuresToZoom: Feature[] = []

      // Only zoom to highlightedFeature when not in an editing mode
      if (!isEditing && mission.highlightedFeature) {
        featuresToZoom.push(mission.highlightedFeature)
      }

      // Always check for a NEW tempZone (zoom on first load, even during editing)
      if (mission.tempZone) {
        const zoneId = mission.tempZone.properties?.id_on_map ?? mission.tempZone.properties?.zone_id
        if (zoomedZoneIdRef.current !== zoneId) {
          zoomedZoneIdRef.current = zoneId
          if (mission.mode !== MISSION_PAGE_MODES.ADJUST_COVERAGE) {
            featuresToZoom.push(mission.tempZone)
          }
        }
      } else {
        zoomedZoneIdRef.current = null
      }

      // Always check for a NEW tempZoneRoute (zoom on first load, even during editing)
      if (mission.tempZoneRoute) {
        const routeId = mission.tempZoneRoute.properties?.id_on_map ?? mission.tempZoneRoute.properties?.zone_id
        if (zoomedRouteIdRef.current !== routeId) {
          zoomedRouteIdRef.current = routeId
          if (mission.mode !== MISSION_PAGE_MODES.ADJUST_COVERAGE) {
            featuresToZoom.push(mission.tempZoneRoute)
          }
        }
      } else {
        zoomedRouteIdRef.current = null
      }

      if (featuresToZoom.length > 0) {
        zoomMapTo(map, featuresToZoom, zoomOffset)
      }
    }, [isView, mission.mode, mission.mapMode, mission.highlightedFeature, mission.tempZone, mission.tempZoneRoute, zoomOffset])

    // Zoom to selected mission in Mission Control / Scenarios
    useEffect(() => {
      const map = mapRef.current?.getMap()
      if (!map || !props.missions || !props.highlightedMissionId) {
        zoomedMissionIdRef.current = null
        return
      }

      // Only zoom if the highlighted mission ID has actually changed
      if (zoomedMissionIdRef.current === props.highlightedMissionId) return
      zoomedMissionIdRef.current = props.highlightedMissionId

      const highlightedMission = props.missions.find(m => m.mission?.id === props.highlightedMissionId)
      if (!highlightedMission) return

      const features: Feature[] = [
        ...highlightedMission.cleaningZones,
        ...highlightedMission.restrictZones,
        ...highlightedMission.cleaningRoutes,
        ...([...(highlightedMission.pointRoutes || []), ...(highlightedMission.transferRoutes || [])] as any),
      ]

      if (features.length > 0) {
        zoomMapTo(map, features, zoomOffset)
      }
    }, [props.highlightedMissionId, props.missions, zoomOffset])

    useEffect(() => {
      const map = mapRef.current?.getMap()
      const layer = mission.geoTiffLayer?.layer

      // only zoom if layer exists, has bbox, and is different from last zoomed layer
      if (!map || !layer?.bbox || globalLastZoomedLayerId === layer.id) return

      const featuresToZoom: Feature[] = [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: layer.bbox.coordinates,
          },
          properties: {},
        },
      ]

      if (mission.mode !== MISSION_PAGE_MODES.ADJUST_COVERAGE) {
        zoomMapTo(map, featuresToZoom, zoomOffset)
      }
      globalLastZoomedLayerId = layer.id
    }, [
      mission.geoTiffLayer?.layer,
      mission.geoTiffLayer?.layer?.bbox,
      mission.geoTiffLayer?.layer?.id,
      zoomOffset,
      mission.mode,
    ])

    // Automatically load lidar map if visible and robot is localized
    useEffect(() => {
      const localizationMode = robotLoc.localizationMode
      const mapName = robotLoc.lidarMapName
      const facilityId = robotLoc.facilityId

      if (localizationMode === undefined) {
        return
      }

      if (!mapName) {
        if (lidarMapLayer.show) {
          dispatch(RobotActions.setShowLidarMap(false))
        }
        if (lidarMapLayer.layer || lidarMapLayer.loadingMapName) {
          dispatch(RobotActions.setLidarMapLayer(null))
          dispatch(RobotActions.setLidarMapLoadingState({ progress: PROGRESS_TYPES.IDLE, name: undefined }))
        }
        return
      }

      if (lidarMapLayer.show && mapName && robotLoc.robotId != null) {
        const currentLayerMatches =
          lidarMapLayer.layer?.name === mapName &&
          lidarMapLayer.layer?.robotId === robotLoc.robotId &&
          lidarMapLayer.layer?.facilityId === facilityId
        const isCurrentlyWorkingOnThisMap =
          lidarMapLayer.loadingMapName === mapName &&
          lidarMapLayer.loadingRobotId === robotLoc.robotId &&
          lidarMapLayer.loadingFacilityId === facilityId &&
          (lidarMapLayer.loadingProgress === PROGRESS_TYPES.WORK ||
            lidarMapLayer.loadingProgress === PROGRESS_TYPES.ERROR)

        if (
          (lidarMapLayer.loadingProgress !== PROGRESS_TYPES.SUCCESS || !currentLayerMatches) &&
          !isCurrentlyWorkingOnThisMap
        ) {
          dispatch(robotThunks.ensureLidarMapLayer(robotLoc.robotId, mapName, facilityId))
        }
      }
    }, [
      lidarMapLayer.show,
      lidarMapLayer.layer,
      robotLoc.localizationMode,
      robotLoc.lidarMapName,
      robotLoc.robotId,
      robotLoc.facilityId,
      lidarMapLayer.layer?.name,
      lidarMapLayer.layer?.robotId,
      lidarMapLayer.layer?.facilityId,
      lidarMapLayer.loadingProgress,
      lidarMapLayer.loadingMapName,
      lidarMapLayer.loadingRobotId,
      lidarMapLayer.loadingFacilityId,
      dispatch,
    ])

    const transferRoutes = useMemo(() => {
      let routes: Array<ITrajectory> = []

      // Combine routes and prioritize userGeometry for display
      const allRoutes = [...mission.missionPoints.routes, ...mission.transferRoutes].map(route => {
        // If route is new (not calculated), show userGeometry (straight line)
        if (
          route.properties.status === TRANSFER_CALCULATING_STATUSES.NEW &&
          route.userGeometry &&
          route.userGeometry.coordinates &&
          route.userGeometry.coordinates.length > 0
        ) {
          return {
            ...route,
            geometry: route.userGeometry,
          }
        }
        // Otherwise (status 'ok', 'error', etc.), show standard geometry (calculated path)
        return route
      })

      routes.push(...allRoutes)
      routes = routes.filter(route => route.id !== mission.editingTransfer?.id)
      return routes
    }, [mission.transferRoutes, mission.missionPoints.routes, mission.editingTransfer?.id])

    const initMap = () => {
      const mapInstance = mapRef.current?.getMap()
      if (mapInstance) {
        setMap(mapInstance)
      }

      const geoTiffLayerFeatures: Feature<Geometry, GeoJsonProperties>[] = mission.geoTiffLayer?.layer?.bbox
        ? [
            {
              type: 'Feature' as const,
              geometry: {
                type: 'Polygon',
                coordinates: mission.geoTiffLayer.layer.bbox.coordinates,
              },
              properties: {},
            },
          ]
        : []

      const features: Feature<Geometry, GeoJsonProperties>[] = [
        ...mission.cleaningZones,
        ...mission.restrictZones,
        ...mission.cleaningRoutes,
        ...transferRoutes,
      ]

      // Only add bbox if we haven't zoomed to this layer yet, OR if there's no other content
      if (globalLastZoomedLayerId !== mission.geoTiffLayer?.layer?.id || features.length === 0) {
        features.push(...geoTiffLayerFeatures)
      }

      zoomMapTo(mapInstance, features, zoomOffset)

      if (isView || !mapInstance) return

      const setHighlightedFeature = (id: string | undefined) => {
        dispatch(MissionActions.setHighlightedFeatureInList(id))
      }
      mapInstance?.on('click', () => setHighlightedFeature(undefined))
      mapInstance?.on('click', 'gl-draw-polygon-fill-cleaning-zone', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'restrict-zones-fill', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'transfer-route-halo-style', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'gl-draw-cleaning-route-launch-point', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'gl-draw-cleaning-route-finish-point1', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'gl-draw-cleaning-routes', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'mission-finish-point1', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'mission-start-point', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'text-layer', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )
      mapInstance?.on('click', 'recorded-route-text-layer', (e: any) =>
        setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
      )

      const interactiveLayers = [
        'gl-draw-polygon-fill-cleaning-zone',
        'restrict-zones-fill',
        'transfer-route-halo-style',
        'gl-draw-cleaning-route-launch-point',
        'gl-draw-cleaning-route-finish-point1',
        'gl-draw-cleaning-routes',
        'mission-finish-point1',
        'mission-start-point',
        'text-layer',
        'recorded-route-text-layer',
      ]

      interactiveLayers.forEach(layer => {
        mapInstance?.on('mouseenter', layer, () => {
          if (mapInstance) mapInstance.getCanvas().style.cursor = 'pointer'
        })
        mapInstance?.on('mouseleave', layer, () => {
          if (mapInstance) mapInstance.getCanvas().style.cursor = ''
        })
      })
    }

    const onMapMoved = () => {
      if (isView) return
      dispatch(MissionActions.setHighlightedFeature(undefined))
    }

    const launchPoints = useMemo(() => {
      return mission.launchPoints.filter(launchPoint => launchPoint !== mission.editingLaunchPoint)
    }, [mission.editingLaunchPoint, mission.launchPoints])

    const restrictZones = useMemo(() => {
      return mission.restrictZones.filter(restrictZone => restrictZone.id !== mission.editingZone?.id)
    }, [mission.editingZone, mission.restrictZones])

    const cleaningZones = useMemo(() => {
      return mission.cleaningZones.filter(cleaningZone => cleaningZone.id !== mission.editingZone?.id)
    }, [mission.editingZone, mission.cleaningZones])

    // const cleaningRoutes = useMemo(() => {
    //   return mission.cleaningRoutes.filter(route => route.properties.zone_id !== mission.editingZoneRoute?.properties.zone_id)
    // }, [mission.editingZoneRoute, mission.cleaningRoutes])

    const startPoint = useMemo(() => {
      if (mission.missionPoints.startPoint !== mission.missionPoints.editingPoint) {
        return mission.missionPoints.startPoint
      }
    }, [mission.missionPoints.editingPoint, mission.missionPoints.startPoint])

    const finishPoint = useMemo(() => {
      if (mission.missionPoints.finishPoint !== mission.missionPoints.editingPoint) {
        return mission.missionPoints.finishPoint
      }
    }, [mission.missionPoints.editingPoint, mission.missionPoints.finishPoint])

    const switchShowGeoTiffLayer = () => {
      if (geoTiffLayer.layer) {
        const show = !geoTiffLayer.show
        dispatch(MissionActions.setShowGeoTiffLayer(show))
        if (show) {
          dispatch(RobotActions.setShowLidarMap(false))
        }
      }
    }

    const switchShowLidarMap = () => {
      const show = !lidarMapLayer.show
      dispatch(RobotActions.setShowLidarMap(show))
      if (show) {
        dispatch(MissionActions.setShowGeoTiffLayer(false))
      }

      const mapName = robotLoc.lidarMapName
      const facilityId = robotLoc.facilityId
      if (show && mapName && robotLoc.robotId != null) {
        // Re-fetch if not loaded yet, or if the map name changed since last load
        const currentLayerMatches =
          lidarMapLayer.layer?.name === mapName &&
          lidarMapLayer.layer?.robotId === robotLoc.robotId &&
          lidarMapLayer.layer?.facilityId === facilityId
        const isCurrentlyWorkingOnThisMap =
          lidarMapLayer.loadingMapName === mapName &&
          lidarMapLayer.loadingRobotId === robotLoc.robotId &&
          lidarMapLayer.loadingFacilityId === facilityId &&
          (lidarMapLayer.loadingProgress === PROGRESS_TYPES.WORK ||
            lidarMapLayer.loadingProgress === PROGRESS_TYPES.ERROR)
        if (
          (lidarMapLayer.loadingProgress !== PROGRESS_TYPES.SUCCESS || !currentLayerMatches) &&
          !isCurrentlyWorkingOnThisMap
        ) {
          dispatch(robotThunks.ensureLidarMapLayer(robotLoc.robotId, mapName, facilityId))
        }
      }
    }

    const overlayItems: IOverlayItem[] = [
      {
        onClick: switchShowGeoTiffLayer,
        active: geoTiffLayer.show,
        disabled: !geoTiffLayer.layer?.urlTiles,
        content: <>Geotiff layer</>,
      },
      ...((props.showLidarMapOption
        ? [
            {
              onClick: switchShowLidarMap,
              active: lidarMapLayer.show,
              disabled: !robotLoc.lidarMapName,
              content: <>Lidar map</>,
            },
          ]
        : []) as IOverlayItem[]),
      ...(props.extraOverlayItems || []),
      ...((mountMissionLayers
        ? [
            {
              onClick: () => dispatch(MissionActions.setShowCleaningZones(!showCleaningZones)),
              active: showCleaningZones,
              disabled: false,
              content: <>Cleaning Zones</>,
            },
            {
              onClick: () => dispatch(MissionActions.setShowRestrictZones(!showRestrictZones)),
              active: showRestrictZones,
              disabled: false,
              content: <>Restrict Zones</>,
            },
          ]
        : []) as IOverlayItem[]),
      ...((!isView
        ? [
            {
              onClick: () => props.onSelectRobotClick && props.onSelectRobotClick(),
              active: !!props.selectedRobot,
              disabled: false,
              content: <>{props.selectedRobot ? `Clear: ${props.selectedRobot.name}` : 'Select a Robot'}</>,
            },
          ]
        : []) as IOverlayItem[]),
    ]

    const aggregatedRestrictZones = useMemo(() => {
      return showRestrictZones ? props.missions?.flatMap(m => m.restrictZones) || [] : []
    }, [props.missions, showRestrictZones])

    const aggregatedCleaningZones = useMemo(() => {
      return showCleaningZones ? props.missions?.flatMap(m => m.cleaningZones) || [] : []
    }, [props.missions, showCleaningZones])

    const aggregatedCleaningRoutes = useMemo(() => {
      return showCleaningZones ? props.missions?.flatMap(m => m.cleaningRoutes) || [] : []
    }, [props.missions, showCleaningZones])

    const aggregatedTransferRoutes = useMemo(() => {
      return showCleaningZones
        ? props.missions?.flatMap(m => [...(m.pointRoutes || []), ...(m.transferRoutes || [])]) || []
        : []
    }, [props.missions, showCleaningZones])

    const aggregatedLaunchPoints = useMemo(() => {
      return showCleaningZones ? props.missions?.flatMap(m => m.launchPoints) || [] : []
    }, [props.missions, showCleaningZones])

    const aggregatedStartPoints = useMemo(() => {
      return showCleaningZones ? props.missions?.map(m => m.startingPoint).filter(Boolean) || [] : []
    }, [props.missions, showCleaningZones])

    const aggregatedFinishPoints = useMemo(() => {
      return showCleaningZones ? props.missions?.map(m => m.finishingPoint).filter(Boolean) || [] : []
    }, [props.missions, showCleaningZones])

    return (
      <MapboxCommon
        ref={mapRef}
        onMapMoved={onMapMoved}
        onMapInited={initMap}
        overlayItems={overlayItems}
        disableOverlayControl={props.disableOverlayControl}
        disableRuler={props.disableRuler}
        disableSearch={props.disableSearch}
        maxZoom={23}
        searchRight={props.searchRight}
        panelOffset={props.panelOffset}
        extraMobileButtonsCount={props.selectedRobot ? 1 : 0}
      >
        <Layer id="top-layer-anchor" type="background" layout={{ visibility: 'none' }} />
        <GeoTiffLayer show={geoTiffLayer.show} />
        <LidarMapLayer show={lidarMapLayer.show} beforeId="robot-image-style" />
        {mountMissionLayers && props.missions !== undefined && (
          <>
            <RestrictZonesOnMap restrictZones={aggregatedRestrictZones} />
            <CleaningZonesOnMap cleaningZones={aggregatedCleaningZones} />
            <CleaningRoutesOnMap cleaningRoutes={aggregatedCleaningRoutes} />
            <TransferRoutesOnMap transferRoutes={aggregatedTransferRoutes} />
            <CleaningPointsOnMap
              cleaningRoutes={aggregatedCleaningRoutes}
              launchPoints={aggregatedLaunchPoints}
              beforeId="top-layer-anchor"
            />
            <MissionStartFinishPoints
              startPoints={aggregatedStartPoints as any}
              finishPoints={aggregatedFinishPoints as any}
              beforeId="top-layer-anchor"
            />
            <CleaningZonesNamesOnMap cleaningZones={aggregatedCleaningZones} />
          </>
        )}
        {mountMissionLayers && props.missions === undefined && (
          <>
            <DrawControl
              userProperties={true}
              displayControlsDefault={false}
              onCreate={onFeatureCreated}
              onUpdate={onFeatureGeometryEdited}
              onDelete={() => {}}
              mode={mission.mapMode}
              tempZone={mission.tempZone}
              tempZoneRoute={mission.tempZoneRoute}
              tempLaunchPoint={mission.tempLaunchPoint}
              tempMissionPoint={mission.missionPoints.tempPoint}
              styles={drawStyles}
              tempTransfer={mission.tempTransfer}
              setEditingTempLaunchPoint={() =>
                dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_LAUNCH_POINT))
              }
              setEditingTempZone={() =>
                dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))
              }
              setEditingTempZoneRoute={() =>
                dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
              }
            />
            <RestrictZonesOnMap restrictZones={showRestrictZones ? restrictZones : []} />
            <CleaningZonesOnMap cleaningZones={showCleaningZones ? cleaningZones : []} />
            <CleaningRoutesOnMap cleaningRoutes={showCleaningZones ? mission.cleaningRoutes : []} />
            <ActiveRouteBufferOnMap tempRoute={mission.tempZoneRoute as any} />
            <TransferRoutesOnMap transferRoutes={showCleaningZones ? transferRoutes : []} />
            <CleaningPointsOnMap
              cleaningRoutes={showCleaningZones ? mission.cleaningRoutes : []}
              launchPoints={showCleaningZones ? launchPoints : []}
              beforeId="top-layer-anchor"
            />
            <MissionStartFinishPoints
              startPoint={showCleaningZones ? startPoint : undefined}
              finishPoint={showCleaningZones ? finishPoint : undefined}
              beforeId="top-layer-anchor"
            />
            <CleaningZonesNamesOnMap cleaningZones={showCleaningZones ? cleaningZones : []} />
          </>
        )}
        {mountMissionLayers && <MissionScenarioServicePointsOnMap map={map} />}
        {!isView && <MissionMapUndoControls />}
        {props.children}
        {props.selectedRobot && <RobotOnMap map={mapRef.current?.getMap() as any} />}
      </MapboxCommon>
    )
  })
)

export default MissionMap
